/**
 * SecretaryTasksPanel — Sprint 6
 *
 * Liste des missions pour la secrétaire (provenant des validations/refus
 * de bilans pré-chimio). Elle voit la mission, l'exécute sur son logiciel
 * clinique externe, puis clique [Marquer fait].
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListTodo, CheckCircle2, Calendar, Syringe, AlertTriangle } from "lucide-react";
import api from "@/api";
import { PRIORITY_META } from "@/lib/medicalDecisionTemplates";

const TASK_CONFIG = {
  SCHEDULE_CHEMO: {
    label: "Planifier chimio",
    icon: Syringe,
    color: "emerald",
  },
  EXECUTE_CAT: {
    label: "Exécuter CAT médecin",
    icon: Calendar,
    color: "red",
  },
  RESCHEDULE_AFTER_CANCELLATION: {
    label: "Replanifier (annulation)",
    icon: Calendar,
    color: "orange",
  },
};

const PRIORITY_BADGE_CLASSES = {
  URGENT: "bg-red-100 text-red-700 border-red-200",
  NORMAL: "bg-amber-50 text-amber-700 border-amber-200",
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function SecretaryTasksPanel() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("PENDING"); // PENDING | DONE | ALL
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/secretary-tasks?status=${filter}&limit=200`);
      setTasks(Array.isArray(data) ? data : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markDone = async (taskId) => {
    setBusyId(taskId);
    try {
      await api.post(`/secretary-tasks/${taskId}/done`);
      toast.success("Mission marquée comme faite");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = tasks.filter(t => t.status === "PENDING").length;

  return (
    <div className="dash-card border-l-4 border-amber-400" data-testid="secretary-tasks-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
          <div className="p-2 bg-amber-50 rounded-xl">
            <ListTodo className="w-5 h-5 text-amber-600" strokeWidth={2} />
          </div>
          Missions
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">
              {pendingCount} en attente
            </span>
          )}
        </h2>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {["PENDING", "DONE", "ALL"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`sec-tasks-filter-${f.toLowerCase()}`}
            >
              {f === "PENDING" ? "À faire" : f === "DONE" ? "Terminées" : "Toutes"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-slate-400 text-sm">Chargement…</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm">Aucune mission {filter === "DONE" ? "terminée" : filter === "PENDING" ? "en attente" : ""}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map(t => {
            const cfg = TASK_CONFIG[t.type] || { label: t.type, icon: ListTodo, color: "slate" };
            const Icon = cfg.icon;
            const isDone = t.status === "DONE";
            return (
              <div
                key={t.id}
                className={`border rounded-xl p-4 transition-all ${isDone ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200 hover:shadow-sm"}`}
                data-testid={`sec-task-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-${cfg.color}-100`}>
                    <Icon className={`w-5 h-5 text-${cfg.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{t.patientName || "Patient"}</span>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full bg-${cfg.color}-100 text-${cfg.color}-700`}>
                        {cfg.label}
                      </span>
                      {t.priority && t.priority !== "NORMAL" && (
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${PRIORITY_BADGE_CLASSES[t.priority] || PRIORITY_BADGE_CLASSES.NORMAL}`}
                          data-testid={`sec-task-priority-${t.id}`}
                        >
                          {t.priority === "URGENT" && <AlertTriangle className="w-3 h-3" />}
                          {(PRIORITY_META[t.priority] || PRIORITY_META.NORMAL).label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">
                      {t.instructions}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span>Par {t.createdByName} ({t.createdByRole})</span>
                      <span>•</span>
                      <span>{new Date(t.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                    </div>
                    {isDone && (
                      <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Terminée par {t.doneByName} le {new Date(t.doneAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    )}
                  </div>
                  {!isDone && (
                    <button
                      onClick={() => markDone(t.id)}
                      disabled={busyId === t.id}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 rounded-lg flex items-center gap-1 flex-shrink-0"
                      data-testid={`sec-task-done-${t.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Fait
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
